import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { txnLabel, formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search } from "lucide-react";

export default function AdminTransactions() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (type !== "all") params.set("type", type);
    if (status !== "all") params.set("status", status);
    if (search) params.set("search", search);
    api.get(`/admin/transactions?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [type, status, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <PageHeader title="All Transactions" desc="Complete ledger with search by Txn ID, UTR, name or email." />

      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="txn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Txn ID / UTR / user…" className="pl-9 bg-slate-900/60 border-slate-700 h-10" />
        </div>
        <Tabs value={type} onValueChange={setType}><TabsList className="bg-slate-900/60">{["all", "deposit", "withdrawal", "adjustment", "dispute_hold", "dispute_release", "reversal"].map((s) => <TabsTrigger key={s} value={s} data-testid={`txn-type-${s}`} className="capitalize">{txnLabel(s)}</TabsTrigger>)}</TabsList></Tabs>
        <Tabs value={status} onValueChange={setStatus}><TabsList className="bg-slate-900/60">{["all", "pending", "approved", "paid", "rejected"].map((s) => <TabsTrigger key={s} value={s} data-testid={`txn-status-${s}`} className="capitalize">{s}</TabsTrigger>)}</TabsList></Tabs>
      </div>

      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Type</TableHead><TableHead className="text-slate-400">Amount</TableHead>
                <TableHead className="text-slate-400">UTR</TableHead><TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Date (IST)</TableHead><TableHead className="text-slate-400">Remark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!rows ? <TableRow><TableCell colSpan={8} className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400 mx-auto" /></TableCell></TableRow> :
                rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">No transactions match.</TableCell></TableRow> :
                rows.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`txn-row-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell className="text-sm">{t.user.name}</TableCell>
                    <TableCell className="capitalize text-sm">{txnLabel(t.type)}{t.direction ? <span className="text-slate-500"> ({t.direction})</span> : ""}</TableCell>
                    <TableCell className={`font-mono ${t.type === "withdrawal" || t.direction === "debit" ? "text-rose-300" : "text-emerald-300"}`}>{formatINR(t.amount)}</TableCell>
                    <TableCell className="font-mono text-xs">{t.utr || "—"}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[160px] truncate" title={t.remark}>{t.remark || "—"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
